/**
 * Adaptive experience engine: derives a privacy-first profile from activity signals
 * (local check-ins + merged local/server Relationship Intelligence events).
 */

import { safeParseJson } from './storage-utils';

export const ADAPTIVE_EVENTS_STORAGE_KEY = 'tension_adaptive_events_v1';
const EVENTS_KEY = ADAPTIVE_EVENTS_STORAGE_KEY;
export const MAX_ADAPTIVE_EVENTS = 250;

export type ActivityFocus = 'repair' | 'communication' | 'patterns' | 'connection' | 'balanced';

export interface PriorityTool {
  id: string;
  sectionId: string;
  label: string;
  reason: string;
}

export interface AdaptiveProfile {
  focus: ActivityFocus;
  headline: string;
  subline: string;
  priorityTools: PriorityTool[];
  dataQuality: { score: number; label: string };
  signalStrength: number;
  enterpriseNotes: string[];
  lastUpdated: number;
}

export type ActivityEvent = {
  type: string;
  at: number;
  meta?: Record<string, unknown>;
  /** Idempotency key for server sync; assigned for all new events */
  clientEventId?: string;
};

function newClientEventId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
}

function eventMergeKey(e: ActivityEvent): string {
  if (e.clientEventId) return `id:${e.clientEventId}`;
  return `legacy:${e.type}:${e.at}:${JSON.stringify(e.meta ?? {})}`;
}

function readCheckins(): Array<{ rtiScore?: number; timestamp?: string; date?: string; triggers?: string[] }> {
  const raw = localStorage.getItem('tension_checkins');
  const parsed = safeParseJson<unknown>(raw, []);
  return Array.isArray(parsed) ? (parsed as Array<{ rtiScore?: number; timestamp?: string; date?: string; triggers?: string[] }>) : [];
}

/** Raw events from localStorage only */
export function readLocalEvents(): ActivityEvent[] {
  const raw = localStorage.getItem(EVENTS_KEY);
  const parsed = safeParseJson<unknown>(raw, []);
  return Array.isArray(parsed)
    ? (parsed as ActivityEvent[]).filter((e) => e && typeof e.at === 'number' && typeof e.type === 'string')
    : [];
}

function writeLocalEvents(events: ActivityEvent[]): void {
  try {
    localStorage.setItem(EVENTS_KEY, JSON.stringify(events.slice(-MAX_ADAPTIVE_EVENTS)));
  } catch {
    /* quota */
  }
}

/** Merge local + server lists; dedupe by clientEventId or legacy key; cap length */
export function mergeActivityEvents(local: ActivityEvent[], server: ActivityEvent[]): ActivityEvent[] {
  const map = new Map<string, ActivityEvent>();
  for (const e of server) {
    map.set(eventMergeKey(e), e);
  }
  for (const e of local) {
    map.set(eventMergeKey(e), e);
  }
  return [...map.values()].sort((a, b) => a.at - b.at).slice(-MAX_ADAPTIVE_EVENTS);
}

/** Append one event locally and return it (includes new clientEventId). */
export function appendLocalAdaptiveEvent(type: string, meta?: Record<string, unknown>): ActivityEvent {
  const clientEventId = newClientEventId();
  const ev: ActivityEvent = { type, at: Date.now(), meta, clientEventId };
  writeLocalEvents([...readLocalEvents(), ev]);
  return ev;
}

/** @deprecated Use appendLocalAdaptiveEvent — kept for direct imports */
export function recordAdaptiveActivity(type: string, meta?: Record<string, unknown>): void {
  appendLocalAdaptiveEvent(type, meta);
}

function countEventTypes(events: ActivityEvent[], prefixes: string[]): number {
  return events.filter((e) => prefixes.some((p) => e.type === p || e.type.startsWith(p))).length;
}

function avgRtiLastN(checkins: ReturnType<typeof readCheckins>, n: number): number | null {
  const slice = checkins.slice(-n);
  const scores = slice.map((c) => (typeof c.rtiScore === 'number' ? c.rtiScore : null)).filter((s): s is number => s != null);
  if (scores.length === 0) return null;
  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
}

const TOOL_CATALOG: Record<string, Omit<PriorityTool, 'reason'>> = {
  tension_delta: { id: 'tension_delta', sectionId: 'ri-delta', label: 'Two-Person Tension Delta' },
  rupture_replay: { id: 'rupture_replay', sectionId: 'ri-rupture', label: 'Rupture-to-Repair Replay' },
  signature_outcomes: { id: 'signature_outcomes', sectionId: 'ri-outcomes', label: 'Signature Lab progression sync' },
  micro_coach: { id: 'micro_coach', sectionId: 'ri-microcoach', label: 'Conversation Micro-Coach' },
  ei_live: { id: 'ei_live', sectionId: 'ri-ei-live', label: 'Real-time EI Coach (Atlas)' },
  attachment_drift: { id: 'attachment_drift', sectionId: 'ri-drift', label: 'Attachment Drift Detection' },
  silent_signals: { id: 'silent_signals', sectionId: 'ri-silent', label: 'Silent Signal Layer' },
  repair_challenge: { id: 'repair_challenge', sectionId: 'ri-challenge', label: 'Repair Challenge Mode' },
  therapist_export: { id: 'therapist_export', sectionId: 'ri-therapist', label: 'Therapist Bridge Export' },
  future_self: { id: 'future_self', sectionId: 'ri-future', label: 'Future-Self Nudge' },
};

function tool(reason: string, key: keyof typeof TOOL_CATALOG): PriorityTool {
  return { ...TOOL_CATALOG[key], reason };
}

/**
 * Computes the adaptive profile from a merged event list (local + server).
 */
export function computeAdaptiveProfileFromEvents(events: ActivityEvent[]): AdaptiveProfile {
  const checkins = readCheckins();
  const now = Date.now();

  const recent7 = avgRtiLastN(checkins, 7);
  const paired = localStorage.getItem('tension_delta_consent') === 'true';

  const nDelta = countEventTypes(events, ['ri_delta_save']);
  const nSilent = countEventTypes(events, ['ri_silent_save']);
  const nCoach = countEventTypes(events, ['ri_microcoach_atlas']);
  const nOutcome = countEventTypes(events, ['ri_outcome_sync']);
  const nTherapist = countEventTypes(events, ['ri_therapist_export']);
  const nChallenge = countEventTypes(events, ['ri_challenge_toggle']);

  let focus: ActivityFocus = 'balanced';
  const notes: string[] = [];

  if (recent7 != null && recent7 >= 58) {
    focus = 'repair';
    notes.push('Elevated RTI trend — prioritize repair loops and grounding.');
  } else if (recent7 != null && recent7 <= 38) {
    focus = 'connection';
    notes.push('Calmer baseline — good window to reinforce connection habits.');
  }

  if (focus === 'balanced' && nCoach > nDelta + 2) {
    focus = 'communication';
    notes.push('You use message coaching often — keep sharpening drafts before send.');
  }

  if (nSilent > 2) {
    notes.push('Silent signal history is building — cancellation and latency trends will get sharper.');
  }

  if (focus === 'balanced' && (nOutcome > 3 || nTherapist > 0)) {
    focus = 'patterns';
    notes.push('Rich outcome history — pattern tools will surface more signal.');
  }

  if (focus === 'balanced' && nDelta > 4 && paired) {
    focus = 'connection';
    notes.push('Active shared delta — keep partner alignment in view.');
  }

  const priorityTools: PriorityTool[] = [];

  if (focus === 'repair') {
    priorityTools.push(
      tool('Matches elevated tension — repeat what worked after spikes.', 'rupture_replay'),
      tool('De-escalate drafts before they ship.', 'micro_coach'),
      tool('Atlas-backed phrasing when stakes are high.', 'ei_live'),
      tool('Lock in a calming anchor for the next spike.', 'future_self'),
    );
  } else if (focus === 'communication') {
    priorityTools.push(
      tool('Your most-used lane — refine tone in real time.', 'micro_coach'),
      tool('Full Atlas coach with guardrails.', 'ei_live'),
      tool('Quantify misalignment early.', 'tension_delta'),
    );
  } else if (focus === 'patterns') {
    priorityTools.push(
      tool('Summarize triggers + repairs for clarity.', 'therapist_export'),
      tool('Spot spike→recovery cycles to repeat.', 'rupture_replay'),
      tool('Structured repair cadence.', 'repair_challenge'),
    );
  } else if (focus === 'connection') {
    priorityTools.push(
      tool('Shared alignment signal for both partners.', 'tension_delta'),
      tool('Subtle behavioral cues without reading messages.', 'silent_signals'),
      tool('Light-touch attachment trend.', 'attachment_drift'),
    );
  } else {
    priorityTools.push(
      tool('Start with shared tension visibility.', 'tension_delta'),
      tool('Preview escalation risk on drafts.', 'micro_coach'),
      tool('Sync repair outcomes across devices.', 'signature_outcomes'),
    );
  }

  const seen = new Set<string>();
  const deduped = priorityTools.filter((p) => {
    if (seen.has(p.sectionId)) return false;
    seen.add(p.sectionId);
    return true;
  });

  let headline = 'Your intelligence layer is learning your rhythm.';
  let subline =
    'We prioritize tools from your RTI trend, partner link, and activity — stored on-device and synced to your account when you sign in.';

  if (focus === 'repair') {
    headline = 'Repair mode: catch spirals earlier this week.';
    subline = 'Higher RTI suggests prioritizing replay, micro-coaching, and future-self grounding.';
  } else if (focus === 'communication') {
    headline = 'Communication mode: draft with confidence.';
    subline = 'You lean on coaching — we surface Micro-Coach and Atlas first.';
  } else if (focus === 'patterns') {
    headline = 'Pattern mode: zoom out from single moments.';
    subline = 'Enough history to make exports and rupture replay meaningful.';
  } else if (focus === 'connection') {
    headline = 'Connection mode: strengthen the shared signal.';
    subline = 'Calmer scores — invest in delta alignment and subtle signals.';
  }

  const checkinCount = checkins.length;
  const eventBoost = Math.min(25, events.length * 0.35);
  const pairedBoost = paired ? 12 : 0;
  const dataScore = Math.min(
    100,
    Math.round(18 + Math.min(45, checkinCount * 1.8) + eventBoost + pairedBoost),
  );

  let dataLabel = 'Building baseline';
  if (dataScore >= 75) dataLabel = 'Strong signal depth';
  else if (dataScore >= 50) dataLabel = 'Good coverage';
  else if (dataScore >= 30) dataLabel = 'Early insights';

  const signalStrength = Math.min(
    1,
    checkinCount / 42 + events.length / 180 + (paired ? 0.12 : 0),
  );

  return {
    focus,
    headline,
    subline,
    priorityTools: deduped.slice(0, 4),
    dataQuality: { score: dataScore, label: dataLabel },
    signalStrength,
    enterpriseNotes: notes.slice(0, 3),
    lastUpdated: now,
  };
}

/** Uses local events only (no server merge). */
export function computeAdaptiveProfile(): AdaptiveProfile {
  return computeAdaptiveProfileFromEvents(readLocalEvents());
}

/** Clear on-device adaptive event history (settings / privacy). */
export function clearLocalAdaptiveEvents(): void {
  try {
    localStorage.removeItem(EVENTS_KEY);
  } catch {
    /* ignore */
  }
}
